import { useConfigControllerGetPublicTenantConfig } from 'korint-sdk';
import { useAuth } from 'korint-sdk/react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { Loader2Icon } from 'lucide-react';

import { Button } from '../../../ui/button';
import { AuthLayout } from '../auth-layout';
import { useAuthRedirect } from '../useAuthRedirect';
import { mfaPreferenceQueryKey } from '../useMfaEnrollment';
import { skipMfaSetupForSession } from '../mfaSetupSkip';
import { TotpSetupForm } from '../totp-setup-form';
import { SmsSetupForm } from '../sms-setup-form';
import { Center, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type Method = 'TOTP' | 'SMS' | null;

// Enforcement is app-level (AuthRequired), not a Cognito sign-in challenge — every pool stays mfa_configuration = "OPTIONAL".
export const MfaSetupView = ({ logo }: { logo: ReactNode }) => {
  const { t } = useTranslation();
  const { authStatus } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { redirectUrl, redirectSearch } = useAuthRedirect();
  const { data: tenantConfig } = useConfigControllerGetPublicTenantConfig();
  const [method, setMethod] = useState<Method>(null);

  if (authStatus === 'configuring') {
    return (
      <Center className="fixed inset-0">
        <Loader2Icon className="w-8 h-8 animate-spin" />
      </Center>
    );
  }

  if (authStatus !== 'authenticated') {
    return <Navigate to={{ pathname: '/auth/sign-in', search: redirectSearch }} replace />;
  }

  const handleDone = async () => {
    // refetchType: 'all' — no active useMfaEnrollment() observer on this route to refetch otherwise
    await queryClient.invalidateQueries({ queryKey: mfaPreferenceQueryKey, refetchType: 'all' });
    navigate(redirectUrl);
  };

  const handleSkip = () => {
    skipMfaSetupForSession();
    navigate(redirectUrl);
  };

  return (
    <AuthLayout title={t('authMfaSetup.title')} logo={logo}>
      {!method && (
        <>
          <Text size="sm" tone="muted" className="mb-4">
            {t('authMfaSetup.subtitle')}
          </Text>
          <Stack gap={3}>
            {tenantConfig?.authentication?.mfa?.sms && (
              <Button className="w-full" variant="outline" onClick={() => setMethod('SMS')}>
                {t('authMfaSetup.chooseSms')}
              </Button>
            )}
            {tenantConfig?.authentication?.mfa?.authenticator && (
              <Button className="w-full" variant="outline" onClick={() => setMethod('TOTP')}>
                {t('authMfaSetup.chooseTotp')}
              </Button>
            )}
            <Button className="w-full" variant="secondary" onClick={handleSkip}>
              {t('authMfaSetup.skip')}
            </Button>
          </Stack>
        </>
      )}

      {method === 'TOTP' && <TotpSetupForm onSuccess={handleDone} onBack={() => setMethod(null)} />}
      {method === 'SMS' && <SmsSetupForm onSuccess={handleDone} onBack={() => setMethod(null)} />}
    </AuthLayout>
  );
};
